import { useState } from 'react';
import { useApp } from '../lib/AppContext';
import { INTERESTS } from '../lib/constants';
import Tag from '../components/Tag';
import Button from '../components/Button';
import Header from '../components/Header';

export default function EditInterests() {
  const { userProfile, navigateTo, updateUserInterests, showToast } = useApp();
  const [selectedInterests, setSelectedInterests] = useState<string[]>(
    userProfile?.interests || []
  );

  const toggleInterest = (interest: string) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(i => i !== interest));
    } else if (selectedInterests.length < 6) {
      setSelectedInterests([...selectedInterests, interest]);
    }
  };

  const handleSave = () => {
    if (selectedInterests.length < 2) {
      showToast('Pick at least two topics so we can personalise your civicbeat.');
      return;
    }
    if (selectedInterests.length > 6) {
      showToast('Try choosing up to six topics so your feed stays meaningful.');
      return;
    }
    updateUserInterests(selectedInterests);
    showToast('Interests updated', 'Your civicbeat feed is now tuned to what matters to you.');
    navigateTo('profile');
  };

  const canSave = selectedInterests.length >= 2;

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title="Edit Interests"
        showBackButton
        onBackClick={() => navigateTo('profile')}
      />

      <div className="px-6 py-8">
        <div className="mb-8">
          <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-2">
            What moves you?
          </h2>
          <p className="text-[14px] text-[#A0A0A0] leading-relaxed">
            Pick the topics that feel closest to you right now. They'll help us bring you songs that reflect your world — and you can update them at any time.
          </p>
        </div>

        <div className="mb-6">
          <div className="mb-4">
            <p className="text-[12px] font-semibold text-[#FF6F61] uppercase tracking-wide">
              Select 2–6 topics
            </p>
            <p className="text-[13px] text-[#A0A0A0] mt-1">
              This helps us understand which issues matter most to you.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map(interest => (
              <Tag
                key={interest}
                selected={selectedInterests.includes(interest)}
                onClick={() => toggleInterest(interest)}
                variant="pill"
              >
                {interest}
              </Tag>
            ))}
          </div>
        </div>

        {(selectedInterests.length < 2 || selectedInterests.length > 6) && (
          <div className="bg-[#181818]/50 backdrop-blur-sm rounded-xl p-4 border border-[#FF6F61]/20">
            <p className="text-[13px] text-[#FF6F61] text-center">
              {selectedInterests.length < 2
                ? 'Pick at least two topics so we can personalise your civicbeat.'
                : 'Try choosing up to six topics so your feed stays meaningful.'}
            </p>
          </div>
        )}

        <div className="mt-8 space-y-4">
          <div className="bg-[#181818] rounded-xl p-5">
            <h3 className="text-[14px] font-semibold text-[#F5F5F5] mb-3">
              How this shapes your experience
            </h3>
            <ul className="space-y-2 text-[13px] text-[#A0A0A0]">
              <li className="flex gap-2">
                <span className="text-[#40E0D0] flex-shrink-0">•</span>
                <span>Your Home feed will prioritise songs connected to these issues</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#40E0D0] flex-shrink-0">•</span>
                <span>Your Meaning Mix playlists will be tuned to your interests</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#40E0D0] flex-shrink-0">•</span>
                <span>Your Profile's 'What Moves You' will update to reflect your choices</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#40E0D0] flex-shrink-0">•</span>
                <span>You'll start seeing patterns in your civic practice based on these selections</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-[#181818] px-6 py-4 border-t border-[#111111]">
        <Button
          fullWidth
          variant="primary"
          onClick={handleSave}
          disabled={!canSave}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
